import React, { useCallback, useContext, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { Trans, useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { ScreenSize, UI } from "@wwimmo/ui";
import { AssemblyRealestate } from "src/stores/assemblies/AssemblyStore";
import styles from "./AssemblyRealestateList.module.css";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { AssemblyRealestateListRowMobile } from "src/screens/assemblies/assembly/details/realestatelist/AssemblyRealestateListRowMobile";
import { AssemblyRealestateListRowDesktop } from "src/screens/assemblies/assembly/details/realestatelist/AssemblyRealestateListRowDesktop";
import { AddAssemblyRealestateButtonAndModal } from "src/components/assemblies/realestates/AddAssemblyRealestateButtonAndModal";
import { MessageType } from "src/components/notifications/Notifier";
import { Route, selectRoute, getParams } from "src/config/routes";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";
import { ErpType } from "src/network/User";

const AssemblyRealestateListBase = () => {
    const { t } = useTranslation();
    const { uiStore, assemblyStore, realestateStore, authStore } = useContext(RootStoreContext);
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const role = authStore.user?.role;
    let params = getParams(Route.assemblyDetails, role);
    const assemblyId = params["assemblyid"];

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const realestateId = queryParams.get("realestateId");

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const onRemoveRealestate = useCallback(
        (removeRealestateId: string) => (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
            event.stopPropagation();
            event.preventDefault();

            const isIT2 = authStore.user?.erpType === ErpType.IT2;
            const allEntries = assemblyStore.currentAssemblyRealestates;

            let realestateIdsToRemove = isIT2
                ? allEntries
                      .filter(
                          (e) =>
                              e.realestate.id === removeRealestateId ||
                              e.realestate.consolidationid === removeRealestateId
                      )
                      .map((e) => e.realestate.id)
                : allEntries.filter((e) => e.realestate.id === removeRealestateId).map((e) => e.realestate.id);

            // remove parent if all childner got removed
            if (isIT2) {
                const parents = allEntries.filter((e) => e.realestate.consolidationid == null);

                const idsToRemoveSet = new Set(realestateIdsToRemove);

                parents.forEach((parent) => {
                    const parentId = parent.realestate.id;

                    const children = allEntries.filter((e) => e.realestate.consolidationid === parentId);

                    const touchedFamily =
                        idsToRemoveSet.has(parentId) || children.some((c) => idsToRemoveSet.has(c.realestate.id));
                    if (!touchedFamily) return;

                    const remainingChildren = children.filter((c) => !idsToRemoveSet.has(c.realestate.id));

                    // If no children left after this removal, also remove the parent
                    if (remainingChildren.length === 0) {
                        idsToRemoveSet.add(parentId);
                    }
                });

                realestateIdsToRemove = Array.from(idsToRemoveSet);
            }

            // remove everything
            realestateIdsToRemove.forEach((realId) => {
                assemblyStore.removeCurrentAssemblyRealestates(realId);
            });

            printStatusMessage(
                t("screens.realestate.assembly.assembly_edit.remove_realestate_success"),
                MessageType.INFO
            );
        },
        [authStore.user?.erpType, assemblyStore, printStatusMessage, t]
    );

    const getColumnWidths = useCallback(() => {
        const columnWidthsDesktop = [1, 1, 1, 0.1];
        const columnWidthsLaptopL = [0.4, 0.8, 0.8, 0.4];
        const columnWidthsLaptop = [0.3, 0.9, 0.8, 0.4];
        const columnWidthsTablet = [0.2, 0.5, 0.5, 0];

        return uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
            ? columnWidthsDesktop
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
              ? columnWidthsLaptopL
              : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP
                ? columnWidthsLaptop
                : columnWidthsTablet;
    }, [uiStore.currentScreenSize]);

    const columnWidths = getColumnWidths();

    useEffect(() => {
        if (realestateId) {
            const realestatesDataCondominium = realestateStore.realestatesCondominium;
            const matchingRealestate = realestatesDataCondominium.find((realestate) => realestate.id === realestateId);

            if (matchingRealestate) {
                // Create the AssemblyRealestate object
                const assemblyRealestate: AssemblyRealestate = {
                    id: uuidv4(),
                    realestate: {
                        id: matchingRealestate.id,
                        number: matchingRealestate.number,
                        name: matchingRealestate.name,
                        consolidationid: matchingRealestate.consolidationid
                    }
                };
                assemblyStore.addCurrentAssemblyRealestates(assemblyRealestate);
            }
        }
    }, [realestateId, realestateStore.realestatesCondominium, assemblyStore, assemblyId]);

    const renderItem = useCallback(
        (assemblyrealestate: AssemblyRealestate, screenSize: ScreenSize) => {
            if (
                assemblyrealestate.id === undefined ||
                assemblyrealestate.id === "" ||
                !assemblyrealestate.realestate.id
            ) {
                return undefined;
            }
            const columnWidths = getColumnWidths();

            const listRows = [
                screenSize === ScreenSize.MOBILE ? (
                    <AssemblyRealestateListRowMobile
                        assemblyrealestates={assemblyrealestate}
                        key={`row-${assemblyrealestate.id}`}
                        onRemoveRealestate={onRemoveRealestate}
                        isAssemblyReadOnly={isReadOnly}
                    />
                ) : (
                    <AssemblyRealestateListRowDesktop
                        assemblyrealestate={assemblyrealestate}
                        key={`row-${assemblyrealestate.id}`}
                        widths={columnWidths}
                        onRemoveRealestate={onRemoveRealestate}
                        isAssemblyReadOnly={isReadOnly}
                    />
                )
            ];
            return (
                <UI.List.Item
                    key={`item-${assemblyrealestate.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                    isClickableItemWithoutChevron={true}
                    to={
                        !assemblyrealestate.isReadOnly && assemblyrealestate.realestate.id
                            ? selectRoute(Route.dashboard, authStore.user?.role, {
                                  realestateid: assemblyrealestate.realestate.id || ""
                              })
                            : undefined
                    }
                />
            );
        },
        [getColumnWidths, onRemoveRealestate, authStore.user?.role, isReadOnly]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }
            const rows = [
                <UI.List.Cell
                    key="realestate"
                    colspan={columnWidths[0]}
                    value={<Trans>screens.realestate.title</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    key="number"
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                    colspan={columnWidths[1]}
                    value={<Trans>screens.realestate.dashboard.number</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    key="address"
                    colspan={columnWidths[2]}
                    value={<Trans>screens.realestate.dashboard.address</Trans>}
                    truncate
                />,
                <UI.List.Cell key="empty" colspan={columnWidths[3]} value={""} truncate />
            ];
            return <UI.List.Header rows={rows} className="p-0" />;
        },
        [columnWidths, uiStore.currentScreenSize]
    );

    const orderedAssemblyRealestates: AssemblyRealestate[] = useMemo(() => {
        const allItems = assemblyStore.currentAssemblyRealestates.slice();
        const isIT2 = authStore.user?.erpType === ErpType.IT2;

        if (!isIT2) {
            return allItems;
        }

        // 1) Separate "parents" vs "children"
        const parents = allItems.filter((e) => e.realestate.consolidationid === null);
        const children = allItems.filter((e) => e.realestate.consolidationid !== null);

        // 2) Build a lookup of parent IDs for quick matching
        const parentIds = new Set(parents.map((p) => p.realestate.id));

        // 3) For each parent, push the parent and then any children whose consolidationid matches that parent's id
        const ordered: AssemblyRealestate[] = [];
        parents.forEach((parentItem) => {
            ordered.push(parentItem);

            const matchingKids = children.filter(
                (childItem) => childItem.realestate.consolidationid === parentItem.realestate.id
            );
            matchingKids.forEach((kid) => ordered.push(kid));
        });

        // 4) Now find any children whose consolidationid is not in parentIds
        const childrenWithoutParent = children.filter(
            (childItem) => !parentIds.has(childItem.realestate.consolidationid!)
        );

        // 5) Append childrenWithoutParent to the bottom
        childrenWithoutParent.forEach((child) => ordered.push(child));

        return ordered;
    }, [assemblyStore.currentAssemblyRealestates, authStore.user?.erpType]);

    return (
        <UI.Container>
            {isReadOnly ? undefined : (
                <UI.Row className="mb-1 assembly-list-header">
                    <UI.Col xs={12} sm={{ span: 9 }}></UI.Col>
                    <UI.Col xs={12} sm={12} md={4} lg={{ span: 3 }} className="d-grid">
                        <AddAssemblyRealestateButtonAndModal />
                    </UI.Col>
                </UI.Row>
            )}
            <UI.Row className="assemblies-list-row">
                <UI.Col xs={12}>
                    <UI.Card>
                        <UI.List.BasicList
                            screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                            items={orderedAssemblyRealestates}
                            renderItem={renderItem}
                            title={t("screens.realestates.title").toString()}
                            renderHeader={renderHeader}
                            itemsPerPage={10}
                            emptyListMessage={
                                assemblyStore.assemblies.length === 0
                                    ? t("screens.assemblies.list.assemblies_realestates_are_loading").toString()
                                    : t("screens.assemblies.list.no_assemblies_realestates_found").toString()
                            }
                        />
                    </UI.Card>
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const AssemblyRealestateList = Sentry.withProfiler(observer(AssemblyRealestateListBase));
